import { useEffect, useState } from 'react';

/**
 * Live `prefers-reduced-motion: reduce` probe with a media-query listener
 * so flipping the OS-level toggle updates the UI without a tab reload.
 *
 * Shared by both tactics-board surfaces (2D + 3D) and any child component
 * that needs to freeze ambient animation. Lifted from TacticsBoard3D's
 * inline implementation in commit-1 of the HUD-unification work so the
 * 2D board can drop its duplicate detectReducedMotion() helper.
 *
 * Returns `false` in non-browser environments (SSR, jsdom without
 * matchMedia) so the caller can render its full-motion path safely.
 */
export function useReducedMotion(): boolean {
  const initial =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? (() => {
          try {
            return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          } catch {
            return false;
          }
        })()
      : false;
  const [reduced, setReduced] = useState<boolean>(initial);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch {
      return;
    }
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    // Older Safari only supports the deprecated addListener path.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return reduced;
}
